import { create, show, useModal } from "@ebay/nice-modal-react";
import { FormProvider, useForm } from "react-hook-form";
import Skeleton from "react-loading-skeleton";

import FormFieldTextConfirm from "components/FormFields/Confirm";
import { Package, Stack } from "components/icons/generated";
import Box from "ds/components/Box";
import DeleteConfirmationModal from "ds/components/DeleteConfirmationModal";
import Icon from "ds/components/Icon";
import Link from "ds/components/Link";
import Typography from "ds/components/Typography";
import { pluralizeNext } from "shared/Pluralize";

import styles from "./styles.module.css";
import useDeleteContext from "./useDeleteContext";

type DeleteConfirmationProps = {
  id: string;
  hasAttachedEntities: boolean;
  name: string;
};

const DeleteConfirmation = create(function DeleteConfirmation({
  id,
  hasAttachedEntities,
  name,
}: DeleteConfirmationProps) {
  const modal = useModal();

  const { attachedEntities, attachmentsError, entityCount, loading, onDelete, pendingAttachments } =
    useDeleteContext(id);

  const contextDeleteForm = useForm<{ deleteConfirmationText: string }>({
    defaultValues: { deleteConfirmationText: "" },
    mode: "onChange",
  });

  const onConfirm = () => {
    onDelete().then(() => {
      modal.resolve(true);
      modal.hide();
    });
  };

  const onClose = () => {
    modal.resolve(false);
    modal.hide();
  };

  if (attachmentsError) {
    onClose();
  }

  return (
    <DeleteConfirmationModal
      title="Delete context"
      onConfirm={onConfirm}
      onClose={onClose}
      isLoading={loading}
      isDismissable={!loading}
      isConfirmationDisabled={
        loading || (hasAttachedEntities && !contextDeleteForm.formState.isValid)
      }
    >
      {hasAttachedEntities ? (
        <Box direction="column" gap="medium">
          <Typography tag="p">
            By deleting <strong>{name}</strong> context you will detach it from{" "}
            {pendingAttachments ? (
              <Skeleton width="5rem" />
            ) : (
              <>
                <strong>{pluralizeNext(attachedEntities.length, "entity", "entities")}</strong>.
              </>
            )}
          </Typography>
          {pendingAttachments && <Skeleton height="44px" />}
          {entityCount.stacks > 0 && (
            <Box justify="between" gap="x-large" padding="large" className={styles.entitySummary}>
              <Box gap="large">
                <Icon src={Stack} name="stacks" />
                <Typography tag="p">Stacks</Typography>
              </Box>
              <strong>{entityCount.stacks}</strong>
            </Box>
          )}
          {entityCount.modules > 0 && (
            <Box justify="between" gap="x-large" padding="large" className={styles.entitySummary}>
              <Box gap="large">
                <Icon src={Package} name="Modules" />
                <Typography tag="p">Modules</Typography>
              </Box>
              <strong>{entityCount.modules}</strong>
            </Box>
          )}
          <Typography tag="p" margin="medium 0">
            Confirm context deletion below or verify it's attached entities on the{" "}
            <Link href={`/context/${id}/used-by`} target="_blank">
              Used by screen
            </Link>
          </Typography>
          <FormProvider {...contextDeleteForm}>
            <FormFieldTextConfirm
              fieldName="deleteConfirmationText"
              textToConfirm={name}
              disabled={loading}
            />
          </FormProvider>
        </Box>
      ) : (
        <Typography tag="p">
          {pendingAttachments ? (
            <Skeleton />
          ) : (
            <>
              Are you sure you want to delete <strong>{name}</strong> context?
            </>
          )}
        </Typography>
      )}
    </DeleteConfirmationModal>
  );
});

export const showDeleteConfirmation = (props: DeleteConfirmationProps) =>
  show(DeleteConfirmation, props);
